import React, { useRef, useState } from 'react';
import { FiUpload, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { FormAlert } from './form';
import { formatMoney } from '../utils/money';

/**
 * P-COB-A: conciliar extracto MP con ventas impagas (sin crear ventas nuevas).
 */
export default function MpCobrosReconcilePanel({
  secureFetch,
  modoOscuro,
  box,
  textPri,
  textMuted,
  onApplied,
}) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const reset = () => {
    setPreview(null);
    setMessage({ type: '', text: '' });
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setLoading(true);
    setMessage({ type: '', text: '' });
    setPreview(null);
    try {
      const csv = await file.text();
      const res = await secureFetch('/api/invoices/import/mercadopago/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No se pudo leer el CSV');

      if (!data.count) {
        setMessage({ type: 'error', text: 'No encontramos cobros en ese archivo.' });
        return;
      }

      setPreview({
        suggestions: data.suggestions || [],
        matchedCount: data.matchedCount || 0,
        unmatchedCount: data.unmatchedCount ?? 0,
        matches: data.matches || [],
        count: data.count,
      });

      if (!data.matchedCount) {
        setMessage({
          type: 'error',
          text: 'Hay cobros en el CSV pero ninguno coincide con una impaga de forma segura. Podés importar todo desde Facturas si querés crear ventas nuevas.',
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Error al analizar el CSV' });
    } finally {
      setLoading(false);
    }
  };

  const applyMatches = async () => {
    if (!preview?.suggestions?.length || !preview.matchedCount) return;
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await secureFetch('/api/invoices/import/mercadopago/apply-matches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: preview.suggestions }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No se pudo aplicar la conciliación');

      setMessage({ type: 'success', text: data.message || 'Conciliación aplicada.' });
      setPreview(null);
      if (typeof onApplied === 'function') onApplied();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Error al conciliar' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-2xl border ${box}`}>
      <div className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 px-4 py-3 border-b ${
        modoOscuro ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <div className="min-w-0">
          <h2 className={`text-sm font-semibold ${textPri}`}>Conciliar cobros de Mercado Pago</h2>
          <p className={`text-xs mt-0.5 ${textMuted}`}>
            Subí el CSV de actividad de MP: marcamos automáticamente las impagas que coinciden (monto y referencia).
            Los cobros con link de pago de Contsuite se registran solos al aprobarse.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFile}
          />
          <button
            type="button"
            disabled={loading}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[#1F80FF] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1866CC] disabled:opacity-60"
          >
            <FiUpload className="w-4 h-4" />
            {loading && !preview ? 'Analizando…' : 'Elegir CSV'}
          </button>
          {preview && (
            <button
              type="button"
              disabled={loading}
              onClick={reset}
              className={`rounded-xl border px-3 py-2 text-xs font-semibold ${
                modoOscuro ? 'border-gray-600 text-gray-200' : 'border-gray-300 text-gray-700'
              }`}
            >
              Cancelar
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <FormAlert type={message.type === 'success' ? 'success' : message.type === 'error' ? 'error' : ''}>
          {message.text}
        </FormAlert>

        {preview && preview.matchedCount > 0 && (
          <div className="space-y-3">
            <p className={`text-xs ${textMuted}`}>
              {preview.count} cobro(s) en el archivo ·{' '}
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                {preview.matchedCount} listo(s) para marcar cobrado
              </span>
              {preview.unmatchedCount > 0 && (
                <span>
                  {' '}· {preview.unmatchedCount} sin match (no se crean ventas desde acá)
                </span>
              )}
            </p>
            <ul className={`text-xs space-y-1.5 max-h-40 overflow-y-auto ${textPri}`}>
              {(preview.matches || []).slice(0, 8).map((m) => (
                <li
                  key={`${m.invoiceId}-${m.referencia}`}
                  className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg px-2 py-1.5 ${
                    modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'
                  }`}
                >
                  <FiCheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span className="font-medium">#{m.numero}</span>
                  <span className={textMuted}>{m.cliente}</span>
                  <span className="tabular-nums">{formatMoney(m.monto, 'ARS')}</span>
                  <span className={`${textMuted} italic`}>{m.reason}</span>
                </li>
              ))}
            </ul>
            {preview.matches?.length > 8 && (
              <p className={`text-[11px] ${textMuted}`}>y {preview.matches.length - 8} más…</p>
            )}
            <button
              type="button"
              disabled={loading}
              onClick={applyMatches}
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {loading ? 'Aplicando…' : `Marcar ${preview.matchedCount} venta(s) como cobrada(s)`}
            </button>
          </div>
        )}

        {preview && !preview.matchedCount && (
          <p className={`flex items-start gap-2 text-xs ${textMuted}`}>
            <FiAlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            Descargá el reporte desde Mercado Pago → Actividad → Exportar. Si cobrás con tu link fijo,
            este paso cierra el circuito con las ventas que ya cargaste en Contsuite.
          </p>
        )}
      </div>
    </div>
  );
}
